import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook to handle breadcrumb navigation events
 * Listens for 'react-breadcrumb-event' and navigates accordingly
 * Handles vision prefix intelligently to avoid conflicts with existing routing
 */
export const useBreadcrumbNavigation = () => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleBreadcrumbEvent = (event: CustomEvent) => {
            try {
                const { path, params } = event.detail;

                if (!path) {
                    console.warn('🍞 No path provided in breadcrumb event');
                    return;
                }

                // Build the navigation path - ensure it goes through the vision routing
                // Since DynamicRoutes handles vision/ prefix via addLeadingVision,
                // we need to prepend /vision/ to match the expected route structure
                let navigationPath;

                // Clean the path first (remove leading slashes)
                const cleanPath = path.replace(/^\/+/, '');

                // Always prepend /vision/ since that's how DynamicRoutes works
                navigationPath = `/vision/${cleanPath}`;

                // Add query parameters if they exist
                if (params && Object.keys(params).length > 0) {
                    const searchParams = new URLSearchParams();
                    Object.entries(params).forEach(([key, value]) => {
                        searchParams.append(key, String(value));
                    });
                    navigationPath += `?${searchParams.toString()}`;
                }

                // Use replace instead of push to avoid back button issues
                navigate(navigationPath, { replace: false });

            } catch (error) {
                console.error('🍞 Error handling breadcrumb navigation:', error);
            }
        };

        // Add event listener for custom breadcrumb events on DOCUMENT
        document.addEventListener('react-breadcrumb-event', handleBreadcrumbEvent as EventListener);

        // Cleanup function
        return () => {
            document.removeEventListener('react-breadcrumb-event', handleBreadcrumbEvent as EventListener);
        };
    }, [navigate, location.pathname]);
};